import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Link } from "react-router-dom";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  size: yup.string().required("Size is required"),
  price: yup.number().required("Price is required").positive("Price must be positive"),
  description: yup.string().required("Description is required"),
});

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [allInventories, setAllInventories] = useState([]);
  const [showModalInventory, setShowModalInventory] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (currentProduct) {
      reset({
        name: currentProduct.name || "",
        size: currentProduct.size || "",
        price: currentProduct.price || "",
        description: currentProduct.description || "",
      });
    }
  }, [currentProduct, reset]);

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data.data);
    } catch (error) {
      console.error("Error fetching products: ", error);
    }
  };

  const handleImageUpload = (event) => {
    setCurrentProduct((prev) => ({
      ...prev,
      files: event.target.files,
    }));
  };

  const handleAddProduct = () => {
    setCurrentProduct({
      name: "",
      size: "",
      price: "",
      description: "",
      images: [],
      files: null,
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setCurrentProduct({ ...product, files: null });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await axios.delete(`/products/${id}`);
        setProducts((prev) => prev.filter((product) => product.id !== id));
        toast.success("Product deleted successfully");
      } catch (error) {
        console.error("Error deleting product: ", error);
        toast.error("Error deleting product");
      }
    }
  };

  const saveProduct = async (data) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("size", data.size);
    formData.append("price", data.price);
    formData.append("description", data.description);
    if (currentProduct?.files) {
      Array.from(currentProduct.files).forEach((file) => {
        formData.append("images[]", file);
      });
    }

    try {
      if (isEditing) {
        await api.post(`/products/${currentProduct.id}/update`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Product updated successfully");
      } else {
        await api.post("/products", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Product added successfully");
      }
      fetchProducts();
      setShowModal(false);
      reset();
    } catch (error) {
      console.error("Error saving product: ", error);
      toast.error("Error saving product");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    if (window.confirm("Are you sure you want to remove this image?")) {
      setCurrentProduct((prev) => {
        const updatedImages = [...prev.images];
        updatedImages.splice(index, 1);
        return { ...prev, images: updatedImages };
      });
    }
  };

  const openInventoryModal = (productId) => {
    setCurrentProduct(productId);
    setShowModalInventory(true);
  };

  const saveInventories = async () => {
    try {
      await axios.post(`/api/products/${currentProduct}/link-inventories`, {
        inventories: selectedInventories,
      });
      toast.success("Inventories linked successfully");
      setShowModalInventory(false);
    } catch (error) {
      console.error("Error linking inventories: ", error);
      toast.error("Error linking inventories");
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Products</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Products</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Update Profile</h5>
                <div>
                  <button className="btn btn-primary mb-3" onClick={handleAddProduct}>
                    Add Product
                  </button>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th>Description</th>
                        <th>Images</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, index) => (
                        <tr key={index}>
                          <td>{product.name}</td>
                          <td>{product.size}</td>
                          <td>${product.price}</td>
                          <td>{product.description}</td>
                          <td>
                            {product.images.map((image, imgIndex) => (
                              <img
                                key={imgIndex}
                                src={`/uploads/${image.image_name}`}
                                alt="Product"
                                style={{ width: "50px", height: "auto" }}
                              />
                            ))}
                          </td>
                          <td>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleEditProduct(product)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {showModal && (
                    <div className="modal fade show d-block">
                      <div className="modal-dialog">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">
                              {isEditing ? "Edit Product" : "Add Product"}
                            </h5>
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() => setShowModal(false)}
                            ></button>
                          </div>
                          <div className="modal-body">
                            <form onSubmit={handleSubmit(saveProduct)}>
                              <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                  {...register("name")}
                                />
                                {errors.name && (
                                  <div className="invalid-feedback">{errors.name.message}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Size</label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.size ? "is-invalid" : ""}`}
                                  {...register("size")}
                                />
                                {errors.size && (
                                  <div className="invalid-feedback">{errors.size.message}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Price</label>
                                <input
                                  type="number"
                                  className={`form-control ${errors.price ? "is-invalid" : ""}`}
                                  {...register("price")}
                                />
                                {errors.price && (
                                  <div className="invalid-feedback">{errors.price.message}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Description</label>
                                <textarea
                                  className={`form-control ${errors.description ? "is-invalid" : ""}`}
                                  {...register("description")}
                                ></textarea>
                                {errors.description && (
                                  <div className="invalid-feedback">{errors.description.message}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Image</label>
                                <input
                                  type="file"
                                  className="form-control"
                                  multiple
                                  onChange={handleImageUpload}
                                />
                              </div>
                              {isEditing && currentProduct?.images?.length > 0 && (
                                <div className="mb-3">
                                  <label className="form-label">Existing Images</label>
                                  <div>
                                    {currentProduct.images.map((image, index) => (
                                      <div key={index} className="d-flex align-items-center mb-2">
                                        <img
                                          src={`/uploads/${image.image_name}`}
                                          alt="Product"
                                          style={{ width: "50px", height: "auto", marginRight: "10px" }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm"
                                          onClick={() => handleRemoveImage(index)}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                {isEditing ? "Update Product" : "Save Product"}
                                {isLoading && (
                                  <span
                                    className="spinner-border spinner-border-sm ms-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                )}
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductManagement;
